import { catchError, finalize, map, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { APP_CONFIG, IAppConfig } from '../../../app.config';
import { MessageService } from '../../../core/service/message.service';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { Action } from '../../model/action';
import { FaultNotification } from '../../model/faultNotification';
import { SelectionItem } from '../../../core/component/select/select.component';
import { Company } from '../../../rent/model/company';
import { Property } from '../../../rent/model/property';
import { Building } from '../../../rent/model/building';
import { Entrance } from '../../../rent/model/entrance';
import { User } from '../../../common/model/user';
import { Category } from '../../model/category';
import { Subcategory } from '../../model/subcategory';
import { FaultNotificationImage } from '../../model/faultNotificationImage';
import { DataServiceErrand } from '../../../core/service/base/data-errand.service';

export class ReopenFaultNotificationModel {
  id: string;
  creditInvoice: boolean;
  revertAccounting: boolean;
  allowed: boolean;
}

@Injectable()
export class FaultNotificationService extends DataServiceErrand<FaultNotification> {
  protected readonly assignMessage: string = 'FAULT_NOTIFICATION.COMMAND.ASSIGN_TO_ME_SUCCESS';
  protected readonly unassignMessage: string = 'FAULT_NOTIFICATION.COMMAND.UNASSIGN_TO_ME_SUCCESS';

  private readonly _defaultFilterFields: string[] = ['status', 'company', 'property', 'building'];

  constructor(
    client: HttpClient,
    @Inject(APP_CONFIG) config: IAppConfig,
    private messageService: MessageService,
    progressBar: ProgressBarService
  ) {
    super(client, config, messageService, FaultNotification, progressBar, 'fault-notification');
  }

  export(filter: any, includedIds: string[], excludedIds: string[], affectedItemsCount: number): Observable<File> {
    return super.exportDataItemsFiltered(filter, includedIds, excludedIds, affectedItemsCount, 'Felanmalningar.xlsx');
  }

  exportWithReporting(filter: any, includedIds: string[], excludedIds: string[], affectedItemsCount: number): Observable<File> {
    return super.exportDataItemsFilteredWithUrl(
      'reporting-export',
      'reporting-export-filter',
      filter,
      includedIds,
      excludedIds,
      affectedItemsCount,
      'Felanmalningar.xlsx'
    );
  }

  get(id: string): Observable<FaultNotification> {
    this.progressBar.start();
    return this.client.get<FaultNotification>(`${this.endpoint}/${id}`).pipe(
      map((context: any) => {
        const model = this.createModel(context);
        if (context.resources) {
          model.resources = context.resources.map(x => new SelectionItem(x.name, x.id));
        }
        return model;
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  override getDefaults(): Observable<FaultNotification> {
    return this.client.get<FaultNotification>(`${this.endpoint}/defaults`).pipe(
      map((context: any) => {
        const model = this.createModel(context);
        if (context.resources) {
          model.resources = context.resources.map(x => new SelectionItem(x.name, x.id));
        }
        return model;
      })
    );
  }

  getByRentalObject(rentalObjectId: string): Observable<FaultNotification[]> {
    return this.client.get<FaultNotification[]>(`${this.endpoint}/rental-object/${rentalObjectId}`).pipe(
      map(data => data.map(item => Object.assign(new FaultNotification(), item))),
      catchError(error => {
        this.messageService.showError(error);
        return [];
      })
    );
  }

  getByContact(contactId: string): Observable<FaultNotification[]> {
    return this.client.get<FaultNotification[]>(`${this.endpoint}/contact/${contactId}`).pipe(
      map(data => data.map(item => Object.assign(new FaultNotification(), item))),
      catchError(error => {
        this.messageService.showError(error);
        return [];
      })
    );
  }

  getFilterDefinition(): Observable<{
    status: number | '';
    company: string;
    companies: Company[];
    number: string;
    property: string;
    properties: Property[];
    municipality: string;
    building: string;
    buildings: Building[];
    entrance: string;
    entrances: Entrance[];
    rentalObjectRentalId: string;
    street: string;
    administrator: string;
    administrators: User[];
    faultNotificationCategoryId: string;
    faultNotificationCategories: Category[];
    faultNotificationSubCategoryId: string;
    faultNotificationSubCategories: Subcategory[];
    tenantName: string;
    tenantPhone: string;
    selectedFields: string[];
    defaultFields: string[];
    executorWebUserId: string;
    executorGroupId: string;
    pets: [];
    unreadMessages: number;
  }> {
    return this.client
      .get<any>(`${this.endpoint}/filter`)
      .pipe(
        map(res => {
          return {
            company: res.company || '',
            companies: Array.isArray(res.companies) ? res.companies.map(x => Object.assign(new Company(), x)) : [],
            number: res.number || '',
            property: res.property || '',
            municipality: res.municipality || '',
            properties: Array.isArray(res.properties) ? res.properties.map(x => Object.assign(new Property(), x)) : [],
            building: res.building || '',
            buildings: Array.isArray(res.buildings) ? res.buildings.map(x => Object.assign(new Building(), x)) : [],
            administrator: res.administrator || '',
            administrators: Array.isArray(res.administrators) ? res.administrators.map(x => Object.assign(new User(), x)) : [],
            entrance: res.entrance || '',
            entrances: Array.isArray(res.entrances) ? res.entrances.map(x => Object.assign(new Entrance(), x)) : [],
            rentalObjectRentalId: res.rentalObjectRentalId || '',
            street: res.street || '',
            faultNotificationCategoryId: res.faultNotificationCategoryId || '',
            faultNotificationCategories: Array.isArray(res.faultNotificationCategories)
              ? res.faultNotificationCategories.map(x => Object.assign(new Category(), x))
              : [],
            faultNotificationSubCategoryId: res.faultNotificationSubCategoryId || '',
            faultNotificationSubCategories: Array.isArray(res.faultNotificationSubCategories)
              ? res.faultNotificationSubCategories.map(x => Object.assign(new Subcategory(), x))
              : [],
            status: Number.isInteger(res.status) ? res.status : '',
            tenantName: res.tenantName || '',
            tenantPhone: res.tenantPhone || '',
            selectedFields: Array.isArray(res.selectedFields) ? res.selectedFields : this._defaultFilterFields,
            defaultFields: this._defaultFilterFields,
            executorWebUserId: res.executorWebUserId || '',
            executorGroupId: res.executorGroupId || '',
            pets: res.pets || [],
            unreadMessages: Number.isInteger(res.unreadMessages) ? res.unreadMessages : 0,
          };
        })
      )
      .pipe(
        catchError(error => {
          console.log(error);
          return [];
        })
      );
  }

  read(id: string): Observable<any> {
    return this.client.put(`${this.endpoint}/log`, JSON.stringify(id));
  }

  unread(id: string): Observable<any> {
    return this.client.put(`${this.endpoint}/unread`, JSON.stringify(id));
  }

  getReopenDefaults(id: string): Observable<ReopenFaultNotificationModel> {
    this.progressBar.start();
    return this.client.get<ReopenFaultNotificationModel>(`${this.endpoint}/reopen-defaults/${id}`).pipe(
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  reopen(model: ReopenFaultNotificationModel): Observable<any> {
    this.progressBar.start();
    return this.client.post<any>(`${this.endpoint}/reopen`, JSON.stringify(model)).pipe(
      map(data => {
        if (data) {
          return { status: data.status, action: Object.assign(new Action(), data.action) };
        }
      }),
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  resolve(id: string): Observable<any> {
    this.progressBar.start();
    return this.client.put<any>(`${this.endpoint}/resolve`, JSON.stringify(id)).pipe(
      map(data => {
        if (data) {
          return { status: data.status, action: Object.assign(new Action(), data.action) };
        }
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  addImage(model: FaultNotificationImage): Observable<any> {
    const formData = new FormData();
    formData.append('faultNotificationId', model.faultNotificationId);
    formData.append('images[]', model.document, model.document.name);
    return this.client.put<any>(`${this.endpoint}/add-image`, formData);
  }

  private createMultiPartFormData(model: FaultNotification, images: File[]): FormData {
    const formData = new FormData();
    formData.append('jsonData', JSON.stringify(model.toJSON()));
    for (let file of images) {
      formData.append('images[]', file, file.name);
    }
    return formData;
  }

  addWithImages(model: FaultNotification, images: File[], options: { notifyUser: boolean } = { notifyUser: true }): Observable<any> {
    this.progressBar.start();
    return this.client.post(this.endpoint, this.createMultiPartFormData(model, images)).pipe(
      tap(() => {
        if (options && options.notifyUser) {
          this.notification.showSuccess();
        }
      }),
      finalize(() => this.progressBar.stop())
    );
  }
}
